import { useEffect, useRef } from "react";

const RECAPTCHA_SRC = "https://www.google.com/recaptcha/api.js";

interface ReCaptchaApi {
  ready(callback: () => void): void;
  render(
    container: HTMLElement,
    parameters: {
      sitekey: string;
      theme?: "light" | "dark";
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
    },
  ): number;
}

declare global {
  interface Window {
    grecaptcha?: ReCaptchaApi;
  }
}

/** Resolves once `grecaptcha.render` can be called. */
function whenRecaptchaReady(): Promise<ReCaptchaApi> {
  return new Promise((resolve) => {
    const ready = () => window.grecaptcha?.ready(() => resolve(window.grecaptcha!));
    if (window.grecaptcha) return ready();

    let script = document.querySelector<HTMLScriptElement>(
      `script[src^="${RECAPTCHA_SRC}"]`,
    );
    if (!script) {
      script = document.createElement("script");
      script.src = `${RECAPTCHA_SRC}?render=explicit`;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", ready, { once: true });
  });
}

/**
 * The MDR form's own reCAPTCHA loader, kept apart from useDeferredRecaptcha so
 * nothing here can change how the site-wide contact form loads its captcha.
 *
 * Like that hook it waits until the form nears the viewport (or is touched). It
 * reuses api.js when the contact form further down /europe already loaded it;
 * otherwise it adds a `?render=explicit` copy, which draws no widget on its own,
 * so the contact form's `.g-recaptcha` is still drawn by the contact form's own
 * copy exactly as on every other page.
 *
 * `render` receives the API once it is ready; returns the ref for the container.
 */
export function useMdrRecaptcha(render: (api: ReCaptchaApi) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    let started = false;

    const start = () => {
      if (started) return;
      started = true;
      detach();
      whenRecaptchaReady().then((api) => {
        if (!cancelled) renderRef.current(api);
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) start();
      },
      { rootMargin: "400px" },
    );
    function detach() {
      io.disconnect();
      el!.removeEventListener("focusin", start);
      el!.removeEventListener("pointerdown", start);
    }

    io.observe(el);
    el.addEventListener("focusin", start);
    el.addEventListener("pointerdown", start);

    return () => {
      cancelled = true;
      detach();
    };
  }, []);

  return ref;
}
